export type MessageSegment =
  | { kind: 'text'; text: string }
  /** A URL into this app, as the route path to navigate to without a page load. */
  | { kind: 'route'; text: string; path: string }
  | { kind: 'url'; text: string; url: string };

// Only http(s), so a message can never produce a `javascript:` or other scheme href.
const urlPattern = /https?:\/\/[^\s<>"]+/gi;
// Prose punctuation right after a URL ("…here: https://x.com/a.") isn't part of it.
const trailingPunctuationPattern = /[.,;:!?'"*]+$/;
const bracketPairs: Record<string, string> = { ')': '(', ']': '[' };

function countOf(text: string, char: string): number {
  return text.split(char).length - 1;
}

/** Drops trailing punctuation and closing brackets the URL didn't open — "(see https://x.com/a)" —
 *  while keeping balanced ones, as in https://en.wikipedia.org/wiki/Lease_(law). */
function trimUrlCandidate(candidate: string): string {
  let url = candidate.replace(trailingPunctuationPattern, '');
  let opener = bracketPairs[url[url.length - 1]];

  while (opener && countOf(url, url[url.length - 1]) > countOf(url, opener)) {
    url = url.slice(0, -1).replace(trailingPunctuationPattern, '');
    opener = bracketPairs[url[url.length - 1]];
  }

  return url;
}

function toLinkSegment(text: string, appOrigin: string): MessageSegment | null {
  let url: URL;

  try {
    url = new URL(text);
  } catch {
    return null;
  }

  if (url.origin === appOrigin) {
    return { kind: 'route', text, path: url.pathname + url.search + url.hash };
  }

  return { kind: 'url', text, url: url.href };
}

/** Splits a plain-text message into text and link segments, so URLs the agent sends render clickable. */
export function splitMessageLinks(content: string, appOrigin: string = window.location.origin): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let textStart = 0;

  for (const match of content.matchAll(urlPattern)) {
    const text = trimUrlCandidate(match[0]);
    const link = toLinkSegment(text, appOrigin);

    if (!link) {
      continue;
    }

    if (match.index > textStart) {
      segments.push({ kind: 'text', text: content.slice(textStart, match.index) });
    }

    segments.push(link);
    textStart = match.index + text.length;
  }

  if (textStart < content.length) {
    segments.push({ kind: 'text', text: content.slice(textStart) });
  }

  return segments;
}
